import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import settingsService from '../../services/settingsService';
import { ageGroupsService, nationsService } from '../../services';
import { getAgeGroupColors } from '../../utils/ageGroupColors';
import ConfirmationModal from './ConfirmationModal';
import Toast from './Toast';

const DataManagementTailwind = () => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [deleteOptions, setDeleteOptions] = useState({
    results: true,
    schedule: true,
    participantGroups: true,
    participantCategories: true,
    groups: true,
    participants: true,
    teamData: true,
    partnerTeams: true,
    categories: false,
    ageGroups: false,
    nations: false
  });
  const [ageGroups, setAgeGroups] = useState([]);
  const [nations, setNations] = useState([]);
  const [demoOptions, setDemoOptions] = useState({
    totalParticipants: 200,
    genderRatio: { M: 50, W: 50 },
    ageGroupRatios: {},
    nationDistribution: 'weighted', // 'equal', 'weighted', oder 'custom'
    nationDistributionMode: 'percent', // 'percent' oder 'absolute'
    nationRatios: {}, // { nationId: percent/count }
    partnerTeamRatio: 15 // 0-30%
  });
  const [showNationConfig, setShowNationConfig] = useState(false);
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [actionType, setActionType] = useState(null); // 'reset-data' | 'seed-demo'
  const [showImportSystemStateConfirm, setShowImportSystemStateConfirm] = useState(false);
  const [importSystemStateData, setImportSystemStateData] = useState(null);
  const [isExportingSystemState, setIsExportingSystemState] = useState(false);
  const [isImportingSystemState, setIsImportingSystemState] = useState(false);

  useEffect(() => {
    loadAgeGroups();
    loadNations();
  }, []);

  const loadAgeGroups = async () => {
    try {
      const res = await ageGroupsService.getAllAgeGroups();
      if (res.success) {
        const allAgeGroups = res.data || [];
        const validAgeGroups = allAgeGroups.filter(ag => ag.name !== 'Alle');

        const alleAgeGroup = allAgeGroups.find(ag => ag.name === 'Alle');

        setAgeGroups(validAgeGroups);

        if (alleAgeGroup && demoOptions.ageGroupRatios && demoOptions.ageGroupRatios[alleAgeGroup.id] !== undefined) {
          const cleanedRatios = { ...demoOptions.ageGroupRatios };
          delete cleanedRatios[alleAgeGroup.id];
          setDemoOptions(prev => ({ ...prev, ageGroupRatios: cleanedRatios }));
        }

        if (!demoOptions.ageGroupRatios || Object.keys(demoOptions.ageGroupRatios).length === 0) {
          const ratios = {};
          let acc = 0;
          validAgeGroups.forEach((ag, idx) => {
            if (idx === validAgeGroups.length - 1) ratios[ag.id] = Math.max(0, 100 - acc);
            else { ratios[ag.id] = 20; acc += 20; }
          });
          setDemoOptions(prev => ({ ...prev, ageGroupRatios: ratios }));
        }
      }
    } catch (e) {
      // still silent
    }
  };

  const loadNations = async () => {
    try {
      const res = await nationsService.getAll();
      if (res.success) {
        setNations(res.data || []);
      }
    } catch (e) {
      // still silent
    }
  };

  const addToast = useCallback((type, message) => {
    setToast({ show: true, message, type });
  }, []);

  const handleResetData = () => {
    setActionType('reset-data');
    setShowActionConfirm(true);
  };

  const performResetData = async () => {
    try {
      setSaving(true);
      const res = await settingsService.resetAllData(deleteOptions);
      if (res.success) {
        addToast('success', res.message || t('admin.dataManagement.toast.resetSuccess'));
      } else {
        addToast('error', res.error || t('admin.dataManagement.toast.resetError'));
      }
    } catch (e) {
      addToast('error', t('admin.dataManagement.toast.resetError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDemo = () => {
    setActionType('seed-demo');
    setShowActionConfirm(true);
  };

  const performSeedDemo = async () => {
    try {
      setSaving(true);

      // Baue die nation distribution basierend auf dem Modus
      let nationDistribution = demoOptions.nationDistribution;
      if (demoOptions.nationDistribution === 'custom') {
        nationDistribution = demoOptions.nationRatios;
      }

      const res = await settingsService.seedDemoData({
        totalParticipants: Number(demoOptions.totalParticipants) || 200,
        genderRatio: demoOptions.genderRatio,
        ageGroupRatios: demoOptions.ageGroupRatios,
        nationDistribution: nationDistribution,
        nationDistributionMode: demoOptions.nationDistributionMode,
        partnerTeamRatio: demoOptions.partnerTeamRatio
      });
      if (res.success) {
        addToast('success', res.message || t('admin.dataManagement.toast.seedSuccess'));
      } else {
        addToast('error', res.error || t('admin.dataManagement.toast.seedError'));
      }
    } catch (e) {
      addToast('error', t('admin.dataManagement.toast.seedError'));
    } finally {
      setSaving(false);
    }
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  const handleExportSystemState = async () => {
    try {
      setIsExportingSystemState(true);
      const result = await settingsService.exportSystemState();
      if (result.success) {
        const exportData = result.data;
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `system-state-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        addToast('success', t('admin.dataManagement.toast.exportSuccess'));
      } else {
        addToast('error', result.error || t('admin.dataManagement.toast.exportError'));
      }
    } catch (e) {
      addToast('error', t('admin.dataManagement.toast.exportError'));
      console.error('Export error:', e);
    } finally {
      setIsExportingSystemState(false);
    }
  };

  const handleImportSystemState = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsImportingSystemState(true);
      const text = await file.text();
      const importData = JSON.parse(text);

      if (!importData.data || !importData.version) {
        addToast('error', t('admin.dataManagement.toast.invalidFile'));
        return;
      }

      setImportSystemStateData(importData);
      setShowImportSystemStateConfirm(true);
    } catch (err) {
      addToast('error', t('admin.dataManagement.toast.readError'));
      console.error('Import error:', err);
    } finally {
      setIsImportingSystemState(false);
      event.target.value = '';
    }
  };

  const handleConfirmImportSystemState = async () => {
    if (!importSystemStateData) return;

    try {
      setIsImportingSystemState(true);
      const result = await settingsService.importSystemState(importSystemStateData);
      if (result.success) {
        addToast('success', t('admin.dataManagement.toast.importSuccess'));
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setIsImportingSystemState(false);
        setShowImportSystemStateConfirm(false);
        setImportSystemStateData(null);
        addToast('error', result.error || t('admin.dataManagement.toast.importError'));
      }
    } catch (e) {
      setIsImportingSystemState(false);
      setShowImportSystemStateConfirm(false);
      setImportSystemStateData(null);
      addToast('error', t('admin.dataManagement.toast.importError'));
      console.error('Import error:', e);
    }
  };

  // Altersklassen-Verteilung für Donutdiagramm vorbereiten
  const ageDistribution = ageGroups
    .filter((ag) => ag.name !== "Alle")
    .map((ag) => {
      const value = demoOptions.ageGroupRatios[ag.id] ?? 0;
      const ageColors = getAgeGroupColors(ag.name);
      return {
        id: ag.id,
        name: ag.name,
        value,
        color: ageColors.hex || "#3b82f6"
      };
    });

  const totalAgeDistribution = ageDistribution.reduce((sum, item) => sum + item.value, 0) || 0;

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t("admin.dataManagement.title")}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5 max-w-3xl leading-relaxed">
            {t("admin.dataManagement.subtitle")}
          </p>
        </header>

        <section className="space-y-6" aria-label={t("admin.dataManagement.title")}>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary-light/15 flex items-center justify-center flex-shrink-0 ring-1 ring-primary/10 dark:ring-primary-light/20">
                <i className="fas fa-seedling text-primary dark:text-primary-light text-sm" aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {t("admin.dataManagement.demo.cardTitle")}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                  {t("admin.dataManagement.demo.cardDesc")}
                </p>
              </div>
            </div>

            <div className="p-5 sm:p-6 lg:p-8">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
                <div className="lg:col-span-5 flex flex-col gap-6">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-600/80 bg-slate-50/70 dark:bg-slate-900/25 p-4 sm:p-5">
                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-4 border-b border-slate-200/90 dark:border-slate-600/60 pb-2">
                      {t("admin.dataManagement.demo.sectionBasics")}
                    </h3>
                    <div className="space-y-5">
                      <div>
                        <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                          {t("admin.dataManagement.demo.totalCount")}
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setDemoOptions((v) => ({ ...v, totalParticipants: Math.max(100, v.totalParticipants - 10) }))}
                            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                            aria-label="-10"
                          >
                            <i className="fas fa-minus text-xs" aria-hidden />
                          </button>
                          <input
                            type="number"
                            min={100}
                            max={1000}
                            value={demoOptions.totalParticipants}
                            onChange={(e) =>
                              setDemoOptions((v) => ({
                                ...v,
                                totalParticipants: Math.max(100, Math.min(1000, Number(e.target.value) || 200)),
                              }))
                            }
                            className="flex-1 h-10 px-3 border border-slate-200 dark:border-slate-600 rounded-xl text-sm tabular-nums text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                          />
                          <button
                            type="button"
                            onClick={() => setDemoOptions((v) => ({ ...v, totalParticipants: Math.min(1000, v.totalParticipants + 10) }))}
                            className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                            aria-label="+10"
                          >
                            <i className="fas fa-plus text-xs" aria-hidden />
                          </button>
                        </div>
                        <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">100–1000</p>
                      </div>

                      <div>
                        <div className="flex items-end justify-between gap-2 mb-2">
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                            {t("admin.dataManagement.demo.genderBalance")}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs font-medium tabular-nums text-slate-600 dark:text-slate-300 mb-2">
                          <span>
                            {t("admin.dataManagement.demo.malePercent")}{" "}
                            <span className="text-primary dark:text-primary-light">{demoOptions.genderRatio.M}%</span>
                          </span>
                          <span>
                            {t("admin.dataManagement.demo.femalePercent")}{" "}
                            <span className="text-primary dark:text-primary-light">{demoOptions.genderRatio.W}%</span>
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={demoOptions.genderRatio.M}
                          onChange={(e) => {
                            const mValue = Number(e.target.value);
                            setDemoOptions((v) => ({
                              ...v,
                              genderRatio: { M: mValue, W: 100 - mValue },
                            }));
                          }}
                          className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-600 appearance-none cursor-pointer accent-primary dark:accent-primary-light"
                        />
                      </div>
                    </div>
                  </div>

                  {ageDistribution.length > 0 && (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-600/80 bg-slate-50/70 dark:bg-slate-900/25 p-4 sm:p-5">
                      <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 border-b border-slate-200/90 dark:border-slate-600/60 pb-2">
                        {t("admin.dataManagement.demo.sectionAge")}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">{t("admin.dataManagement.demo.ageDistribution")}</p>

                      <div className="mb-4">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                            {t("admin.dataManagement.demo.agePreview")}
                          </span>
                          <span
                            className={`text-sm font-bold tabular-nums ${
                              totalAgeDistribution === 100
                                ? "text-emerald-600 dark:text-emerald-400"
                                : totalAgeDistribution > 0
                                  ? "text-amber-600 dark:text-amber-400"
                                  : "text-slate-500 dark:text-slate-400"
                            }`}
                          >
                            {t("admin.dataManagement.demo.sum")} {totalAgeDistribution}%
                          </span>
                        </div>
                        <div
                          className="flex h-3 w-full overflow-hidden rounded-full bg-slate-200/90 dark:bg-slate-700 ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                          role="img"
                          aria-label={t("admin.dataManagement.demo.ageDistribution")}
                        >
                          {totalAgeDistribution > 0 ? (
                            ageDistribution.map((item) => {
                              const w = (item.value / totalAgeDistribution) * 100;
                              if (w <= 0) return null;
                              return (
                                <div
                                  key={item.id}
                                  className="h-full min-w-[2px] transition-[width] duration-200"
                                  style={{ width: `${w}%`, backgroundColor: item.color }}
                                  title={`${item.name}: ${item.value}%`}
                                />
                              );
                            })
                          ) : (
                            <div className="h-full w-full bg-slate-300/50 dark:bg-slate-600/50" />
                          )}
                        </div>
                        {totalAgeDistribution > 0 && totalAgeDistribution !== 100 && (
                          <p className="mt-2 text-xs text-amber-700/90 dark:text-amber-400/90 leading-relaxed">
                            {t("admin.dataManagement.demo.ageSumRecommend")}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {ageDistribution.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-stretch gap-3 rounded-xl border border-slate-200/90 dark:border-slate-600/60 bg-white dark:bg-slate-800/50 p-3 shadow-sm"
                          >
                            <div className="w-1 self-stretch shrink-0 rounded-full" style={{ backgroundColor: item.color }} aria-hidden />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.name}</p>
                              <div className="mt-2 flex items-center gap-2">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={demoOptions.ageGroupRatios[item.id] ?? 0}
                                  onChange={(e) =>
                                    setDemoOptions((v) => ({
                                      ...v,
                                      ageGroupRatios: {
                                        ...v.ageGroupRatios,
                                        [item.id]: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                                      },
                                    }))
                                  }
                                  className="h-9 w-full max-w-[5.5rem] px-2.5 border border-slate-200 dark:border-slate-600 rounded-xl text-sm tabular-nums text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20"
                                />
                                <span className="text-xs font-medium text-slate-500">%</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-7 flex flex-col gap-6">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-600/80 bg-slate-50/70 dark:bg-slate-900/25 p-4 sm:p-5">
                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-4 border-b border-slate-200/90 dark:border-slate-600/60 pb-2">
                      {t("admin.dataManagement.demo.sectionNations")}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{t("admin.dataManagement.demo.nationDist")}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {[
                        { value: "weighted", labelKey: "nationWeighted" },
                        { value: "equal", labelKey: "nationEqual" },
                        { value: "custom", labelKey: "nationCustom" },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`relative flex flex-col items-center justify-center text-center gap-0 min-h-[4.5rem] px-3 py-3 rounded-xl border cursor-pointer transition-all ${
                            demoOptions.nationDistribution === option.value
                              ? "border-primary bg-white shadow-sm ring-2 ring-primary/20 dark:border-primary-light dark:bg-slate-800 dark:ring-primary-light/25"
                              : "border-slate-200 dark:border-slate-600 bg-white/60 dark:bg-slate-800/40 hover:border-slate-300 dark:hover:border-slate-500"
                          }`}
                        >
                          <input
                            type="radio"
                            name="nationDistribution"
                            value={option.value}
                            checked={demoOptions.nationDistribution === option.value}
                            onChange={(e) => setDemoOptions((v) => ({ ...v, nationDistribution: e.target.value }))}
                            className="sr-only"
                          />
                          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100 leading-tight">
                            {t(`admin.dataManagement.demo.${option.labelKey}`)}
                          </span>
                        </label>
                      ))}
                    </div>

                    {demoOptions.nationDistribution === "custom" && (
                      <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/60 space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            {t("admin.dataManagement.demo.inputMode")}
                          </span>
                          <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-600 p-0.5 bg-slate-50 dark:bg-slate-900/40">
                            <button
                              type="button"
                              onClick={() => setDemoOptions((v) => ({ ...v, nationDistributionMode: "percent" }))}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                demoOptions.nationDistributionMode === "percent"
                                  ? "bg-primary text-white dark:bg-primary-light"
                                  : "text-slate-600 dark:text-slate-400 hover:bg-white/80 dark:hover:bg-slate-800"
                              }`}
                            >
                              {t("admin.dataManagement.demo.percent")}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDemoOptions((v) => ({ ...v, nationDistributionMode: "absolute" }))}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                                demoOptions.nationDistributionMode === "absolute"
                                  ? "bg-primary text-white dark:bg-primary-light"
                                  : "text-slate-600 dark:text-slate-400 hover:bg-white/80 dark:hover:bg-slate-800"
                              }`}
                            >
                              {t("admin.dataManagement.demo.absolute")}
                            </button>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowNationConfig(!showNationConfig)}
                          className="w-full flex items-center justify-between px-4 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/30 text-sm font-medium text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                        >
                          <span>{t("admin.dataManagement.demo.configureNations", { count: nations.length })}</span>
                          <i className={`fas fa-chevron-${showNationConfig ? "up" : "down"} text-xs text-slate-400`} aria-hidden />
                        </button>
                        {showNationConfig && (
                          <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-800/80">
                            {nations.map((nation) => (
                              <div key={nation.id} className="flex items-center justify-between gap-2 px-3 py-2">
                                <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                  <span className="font-mono text-xs text-slate-500 mr-2">{nation.code}</span>
                                  {nation.name}
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  <input
                                    type="number"
                                    min={0}
                                    max={demoOptions.nationDistributionMode === "percent" ? 100 : demoOptions.totalParticipants}
                                    value={demoOptions.nationRatios[nation.id] ?? 0}
                                    onChange={(e) =>
                                      setDemoOptions((v) => ({
                                        ...v,
                                        nationRatios: {
                                          ...v.nationRatios,
                                          [nation.id]: Math.max(0, Number(e.target.value) || 0),
                                        },
                                      }))
                                    }
                                    className="w-[4.25rem] h-9 px-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm tabular-nums bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                  />
                                  <span className="text-xs text-slate-500 w-4">{demoOptions.nationDistributionMode === "percent" ? "%" : ""}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-200 dark:border-slate-600/80 bg-slate-50/70 dark:bg-slate-900/25 p-4 sm:p-5">
                    <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3 border-b border-slate-200/90 dark:border-slate-600/60 pb-2">
                      {t("admin.dataManagement.demo.sectionPartner")}
                    </h3>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t("admin.dataManagement.demo.partnerShare")}{" "}
                      <span className="tabular-nums text-primary dark:text-primary-light">({demoOptions.partnerTeamRatio}%)</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="30"
                      value={demoOptions.partnerTeamRatio}
                      onChange={(e) => setDemoOptions((v) => ({ ...v, partnerTeamRatio: Number(e.target.value) }))}
                      className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-600 appearance-none cursor-pointer accent-primary dark:accent-primary-light"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">{t("admin.dataManagement.demo.partnerHint")}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={handleSeedDemo}
                  disabled={saving}
                  className="w-full sm:w-auto min-w-[220px] inline-flex h-11 items-center justify-center gap-2 px-6 bg-primary dark:bg-primary-light text-white text-sm font-semibold rounded-xl shadow-sm hover:bg-primary-dark dark:hover:bg-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className={`fas ${saving ? "fa-spinner fa-spin" : "fa-bolt"} text-xs`} aria-hidden />
                  {saving ? t("admin.dataManagement.demo.working") : t("admin.dataManagement.demo.generate")}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-red-200 dark:border-red-800/80 bg-white dark:bg-slate-800 shadow-sm overflow-hidden ring-1 ring-red-100/80 dark:ring-red-950/40">
              <div className="px-5 py-4 border-b border-red-100 dark:border-red-900/40 bg-red-50/80 dark:bg-red-950/25 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400 text-sm" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-red-900 dark:text-red-100">
                    {t("admin.dataManagement.reset.cardTitle")}
                  </h2>
                  <p className="text-sm text-red-800/90 dark:text-red-200/90 mt-0.5">
                    {t("admin.dataManagement.reset.cardWarn")}
                  </p>
                </div>
              </div>

              <div className="p-5 sm:p-6">
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-3">
                  {t("admin.dataManagement.reset.selectLabel")}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                  {['results', 'schedule', 'participantGroups', 'participantCategories', 'groups', 'participants', 'teamData', 'partnerTeams'].map((key) => (
                    <label
                      key={key}
                      className="flex items-center gap-3.5 p-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/50 cursor-pointer hover:border-slate-300 dark:hover:border-slate-500 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-red-500/30"
                    >
                      <div className="relative flex h-[22px] w-[22px] shrink-0 items-center justify-center">
                        <input
                          type="checkbox"
                          checked={!!deleteOptions[key]}
                          onChange={() => setDeleteOptions((v) => ({ ...v, [key]: !v[key] }))}
                          className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                        />
                        <span className="pointer-events-none flex h-[22px] w-[22px] rounded-full border-2 border-slate-300 bg-white transition-all dark:border-slate-500 dark:bg-slate-800 peer-checked:border-red-600 peer-checked:bg-red-600 peer-checked:shadow-sm dark:peer-checked:border-red-500 dark:peer-checked:bg-red-600 peer-checked:[&>i]:opacity-100 peer-checked:[&>i]:scale-100">
                          <i className="fas fa-check m-auto text-[9px] leading-none text-white opacity-0 scale-50 transition-all duration-150" aria-hidden />
                        </span>
                      </div>
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug">
                        {t(`admin.dataManagement.delete.${key}`)}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/25 px-3 py-2.5 mb-5 flex gap-2.5">
                  <i className="fas fa-info-circle text-slate-400 dark:text-slate-500 text-xs mt-0.5 shrink-0" aria-hidden />
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t("admin.dataManagement.reset.depsHint")}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleResetData}
                  disabled={saving}
                  className="w-full inline-flex h-11 items-center justify-center gap-2 px-4 bg-red-600 dark:bg-red-700 text-white text-sm font-semibold rounded-xl hover:bg-red-700 dark:hover:bg-red-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-trash-alt'} text-xs`} aria-hidden />
                  {saving ? t("admin.dataManagement.demo.working") : t("admin.dataManagement.reset.cta")}
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700/60 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/35 flex items-center justify-center flex-shrink-0">
                  <i className="fas fa-database text-violet-600 dark:text-violet-400 text-sm" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {t("admin.dataManagement.system.cardTitle")}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                    {t("admin.dataManagement.system.cardDesc")}
                  </p>
                </div>
              </div>

              <div className="p-5 sm:p-6 space-y-3">
                <button
                  type="button"
                  onClick={handleExportSystemState}
                  disabled={isExportingSystemState || isImportingSystemState}
                  className="w-full inline-flex h-11 items-center justify-center gap-2 px-4 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className={`fas ${isExportingSystemState ? 'fa-spinner fa-spin' : 'fa-download'} text-xs`} aria-hidden />
                  {isExportingSystemState ? t("admin.dataManagement.system.working") : t("admin.dataManagement.system.export")}
                </button>
                <label className={`w-full inline-flex h-11 items-center justify-center gap-2 px-4 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/80 transition-colors focus-within:ring-2 focus-within:ring-primary/25 ${isExportingSystemState || isImportingSystemState ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <i className={`fas ${isImportingSystemState ? 'fa-spinner fa-spin' : 'fa-upload'} text-xs`} aria-hidden />
                  {isImportingSystemState ? t("admin.dataManagement.system.working") : t("admin.dataManagement.system.import")}
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={handleImportSystemState}
                    className="sr-only"
                    disabled={isExportingSystemState || isImportingSystemState}
                    aria-label={t("admin.dataManagement.system.import")}
                  />
                </label>

                <div className="rounded-xl border border-violet-200 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/20 px-3 py-2.5 flex gap-2.5 mt-2">
                  <i className="fas fa-info-circle text-violet-500 dark:text-violet-400 text-xs mt-0.5 shrink-0" aria-hidden />
                  <p className="text-xs text-violet-900 dark:text-violet-200/90 leading-relaxed">
                    {t("admin.dataManagement.system.warnHint")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />

      <ConfirmationModal
        isOpen={showActionConfirm}
        onClose={() => setShowActionConfirm(false)}
        onConfirm={() => {
          if (actionType === 'reset-data') {
            void performResetData();
          } else {
            void performSeedDemo();
          }
        }}
        title={actionType === 'reset-data' ? t('admin.dataManagement.confirmReset.title') : t('admin.dataManagement.confirmSeed.title')}
        subtitle={actionType === 'reset-data' ? t('admin.dataManagement.confirmReset.subtitle') : t('admin.dataManagement.confirmSeed.subtitle')}
        message={actionType === 'reset-data' ? t('admin.dataManagement.confirmReset.message') : t('admin.dataManagement.confirmSeed.message')}
        confirmText={actionType === 'reset-data' ? t('admin.dataManagement.confirmReset.confirm') : t('admin.dataManagement.confirmSeed.confirm')}
        cancelText={t('admin.dataManagement.cancel')}
        type={actionType === 'reset-data' ? 'danger' : 'info'}
        layout="inline"
        isLoading={saving}
      />

      <ConfirmationModal
        isOpen={showImportSystemStateConfirm}
        onClose={() => {
          setShowImportSystemStateConfirm(false);
          setImportSystemStateData(null);
        }}
        onConfirm={handleConfirmImportSystemState}
        title={t('admin.dataManagement.importModal.title')}
        subtitle={t('admin.dataManagement.importModal.subtitle')}
        message={t('admin.dataManagement.importModal.message')}
        confirmText={t('admin.dataManagement.importModal.confirm')}
        cancelText={t('admin.dataManagement.cancel')}
        type="danger"
        layout="inline"
        isLoading={isImportingSystemState}
      />
    </main>
  );
};

export default DataManagementTailwind;
